"use client";

import React from "react";
import { Button } from "../common/button";

export interface HeroSectionProps {
  trustedByCount?: string;
  title: string;
  subtitle: string;
  ctaText: string;
  onCtaClick?: () => void;
  images?: {
    left: string;
    middle: string;
    right: string;
  };
  showTrustAvatars?: boolean;
}

export function HeroSection({
  trustedByCount = "38,000+",
  title,
  subtitle,
  ctaText,
  onCtaClick,
  images,
  showTrustAvatars = true
}: HeroSectionProps) {
  return (
    <section className="relative overflow-hidden py-8 sm:py-12 md:py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="max-w-xl order-2 lg:order-1 text-center lg:text-left pt-4 lg:pt-0">
            {/* Trust avatars */}
            {showTrustAvatars && (
              <>
                <div className="flex items-center justify-center lg:justify-start gap-1 mb-2">
                  <div className="flex -space-x-2">
                    <img className="w-8 h-8 rounded-full border-2 border-white" src="https://i.pravatar.cc/100?img=49" alt="User" />
                    <img className="w-8 h-8 rounded-full border-2 border-white" src="https://i.pravatar.cc/100?img=32" alt="User" />
                    <img className="w-8 h-8 rounded-full border-2 border-white" src="https://i.pravatar.cc/100?img=47" alt="User" />
                    <img className="w-8 h-8 rounded-full border-2 border-white" src="https://i.pravatar.cc/100?img=28" alt="User" />
                    <img className="w-8 h-8 rounded-full border-2 border-white" src="https://i.pravatar.cc/100?img=44" alt="User" />
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">Trusted by {trustedByCount} Canadians</p>
              </>
            )}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {title}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 mb-8">
              {subtitle}
            </p>
            <div className="flex justify-center lg:justify-start">
              <Button size="lg" className="w-full sm:w-auto" onClick={onCtaClick}>
                {ctaText}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            </div>
          </div>
          
          {/* Right Images */}
          {images && (
            <div className="relative flex gap-2 sm:gap-3 lg:gap-4 items-start justify-center order-1 lg:order-2 w-full max-w-md mx-auto lg:max-w-none">
              <div className="w-28 sm:w-36 lg:w-48 h-[250px] sm:h-[350px] lg:h-[400px]">
                <img 
                  src={images.left}
                  alt="Happy client"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
              <div className="w-28 sm:w-36 lg:w-48 h-[250px] sm:h-[350px] lg:h-[400px] mt-6 sm:mt-8 lg:mt-12">
                <img 
                  src={images.middle}
                  alt="Professional therapist"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
              <div className="w-28 sm:w-36 lg:w-48 h-[250px] sm:h-[350px] lg:h-[400px]">
                <img 
                  src={images.right}
                  alt="Therapy session"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}